import * as React from 'react';

import cn from '@/utils/cn';

import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';

type ExtendedProps = React.ComponentPropsWithoutRef<
  typeof ScrollAreaPrimitive.Root
> & {
  mah?: string;
  thumbClassName?: string;
  barClassName?: string;
  viewportRef?: React.Ref<HTMLDivElement>;
};

const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  ExtendedProps
>(
  (
    {
      className,
      children,
      mah,
      thumbClassName,
      barClassName,
      viewportRef,
      ...props
    },
    ref
  ) => (
    <ScrollAreaPrimitive.Root
      ref={ref}
      className={cn('relative', className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport
        ref={viewportRef}
        className={cn('h-auto w-full rounded-[inherit]', mah)}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar className={barClassName} thumbClassName={thumbClassName} />
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  )
);
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

type ExtendedScrollBarProps = React.ComponentPropsWithoutRef<
  typeof ScrollAreaPrimitive.ScrollAreaScrollbar
> & { thumbClassName?: string };

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  ExtendedScrollBarProps
>(({ className, orientation = 'vertical', thumbClassName, ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    forceMount
    className={cn(
      'rounded-full bg-gray-200 py-1 duration-150',
      orientation === 'vertical' && 'h-full w-2 p-[2px] py-1',
      orientation === 'horizontal' && 'h-2.5 border-t p-[1px]',
      className
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb
      className={cn(
        'relative rounded-sm bg-blue-500 transition-colors hover:bg-blue-600',
        thumbClassName
      )}
    />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

export { ScrollArea, ScrollBar };
